import { Component, forwardRef, Output, EventEmitter, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, Validator, NG_VALIDATORS, AbstractControl, ValidationErrors } from '@angular/forms';
import { AvailabilitySlot, AvailabilitySlotType, emptyAvailabilitySlot, DiscountTarget, DetailsType, mapDiscountTargetToAvailabilitySlotTarget, mapAvailabilitySlotTargetToDiscountTarget, AvailabilitySlotTarget } from '../interfaces/availability-slots.inferfaces';
import { SelectOption, getSlotTypesDropdown, getDiscountTargetDropdown, getSelectedDays, setAvailabilitySlotDay, getOfferDetailsTypesDropdown } from '../interfaces/offer.dropdowns';
import * as moment from 'moment';

// Format is "<number>% off <target>" where target is one of "food", "drinks", "total bill"
const PERCENT_OFF_REGEX = /^(\d+)% off (food|drinks|total bill)$/;

@Component({
  selector: 'ch-availability-item',
  templateUrl: './availability-item.component.html',
  styleUrls: ['./availability-item.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AvailabilityItemComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AvailabilityItemComponent),
      multi: true,
    },
  ],
})
export class AvailabilityItemComponent implements ControlValueAccessor, Validator {
  @Output() cancel: EventEmitter<any> = new EventEmitter();
  private _slot: AvailabilitySlot = emptyAvailabilitySlot();

  slotTypes: SelectOption<AvailabilitySlotType>[];

  checked: 0 | 1 | 2 = 0;
  _detailsType: DetailsType = DetailsType.OTHER;
  detailsTypes: SelectOption<DetailsType>[];

  _percentOff: number;
  _discountTarget: DiscountTarget;
  discountTargets: SelectOption<DiscountTarget>[];

  onChange = (_: any) => { };
  onTouched = () => { };

  constructor() {
    this.slotTypes = getSlotTypesDropdown;
    this.detailsTypes = getOfferDetailsTypesDropdown;
    this.discountTargets = getDiscountTargetDropdown;
  }

  ngDoCheck() {
    if (this.checked === 2) {
      return;
    }
    this.checked++;

    if (this._slot.offerDiscountType === DetailsType.PERCENT_OFF) {
      const matches = PERCENT_OFF_REGEX.exec(this._slot.details);
      this._detailsType = DetailsType.PERCENT_OFF;
      this._percentOff = this._slot.percentage;
      this._discountTarget = mapAvailabilitySlotTargetToDiscountTarget(this._slot.offerDiscountTarget);
    } else {
      this._detailsType = DetailsType.OTHER;
    }
  }

  set slot(newSlot: AvailabilitySlot) {
    this.writeValue(newSlot);
    this.onChange(newSlot);
  }

  get slot(): AvailabilitySlot {
    return this._slot;
  }

  writeValue(newElement: any): void {
    if (newElement !== this._slot) {
      this._slot = newElement;
      // Ensure termsAndConditions is always an array
      if (this._slot && !this._slot.termsAndConditions) {
        this._slot.termsAndConditions = [];
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(_: boolean): void { }

  validate(_: AbstractControl): ValidationErrors {
    return null;
  }

  public updateStartTime(newStartTime: string): void {
    this.slot.startTime = moment(newStartTime).format('HH:mm');
  }

  public updateEndTime(newEndTime: string): void {
    this.slot.endTime = moment(newEndTime).format('HH:mm');
  }

  public daysValues(): string[] {
    return getSelectedDays(this.slot);
  }

  public daySelected({ value, checked }): void {
    setAvailabilitySlotDay(this.slot, value, checked);
  }

  public deleteSlot() {
    this.cancel.emit();
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public updateTermsAndConditionsItem(index: number, value: string): void {
    if (this.slot.termsAndConditions && this.slot.termsAndConditions.length > index) {
      this.slot.termsAndConditions[index] = value;
    }
  }

  public addTermsAndConditionsItem(): void {
    if (!this.slot.termsAndConditions) {
      this.slot.termsAndConditions = [];
    }
    this.slot.termsAndConditions.push('');
  }

  public removeTermsAndConditionsItem(index: number): void {
    if (this.slot.termsAndConditions && this.slot.termsAndConditions.length > index) {
      this.slot.termsAndConditions.splice(index, 1);
    }
  }

  set detailsType(value: DetailsType) {
    this._detailsType = value;

    if (this._detailsType === DetailsType.PERCENT_OFF) {
      if (PERCENT_OFF_REGEX.test(this.slot.details)) {
        const matches = PERCENT_OFF_REGEX.exec(this.slot.details);
        this._percentOff = parseInt(matches[1], 10);
        this._discountTarget = matches[2] as DiscountTarget;
      } else {
        this._percentOff = null;
        this._discountTarget = null;
      }
    } else {
      this.updateDetails();
    }
  }

  get detailsType(): DetailsType {
    return this._detailsType;
  }

  get isDetailsTypeOther() {
    return this._detailsType === DetailsType.OTHER;
  }

  set percentOff(value: number) {
    this._percentOff = value;
    this.updateDetails();
  }

  get percentOff(): number {
    return this._percentOff;
  }

  set discountTarget(value: DiscountTarget) {
    this._discountTarget = value;
    this.updateDetails();
  }

  get discountTarget(): DiscountTarget {
    return this._discountTarget;
  }

  private updateDetails() {
    if (this._percentOff && this._percentOff > 0 && this._percentOff < 100 && this._discountTarget) {
      this.slot.details = `${this._percentOff}% off ${this._discountTarget}`;
      this.slot.offerDiscountType = DetailsType.PERCENT_OFF
      this.slot.percentage = this._percentOff
      this.slot.offerDiscountTarget = mapDiscountTargetToAvailabilitySlotTarget(this._discountTarget)
    } else {
      this.slot.details = null;
      this.slot.offerDiscountType = DetailsType.OTHER
    }
  }
}



// WEBPACK FOOTER //
// /Users/thxgg/Projects/Work/code-hospitality-cms/src/app/offers/availability-item/availability-item.component.ts